import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, Button, Avatar, Tooltip, Modal } from "@mui/material";
import styled from "styled-components";
import useAuth from "../../../hooks/useAuth";
import {
  BsTwitch,
  BsYoutube,
  BsTwitter,
  BsFacebook,
  BsInstagram,
} from "react-icons/bs";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAxiosPrivateForm from "../../../hooks/useAxiosPrivateForm";
import JsonData from "../../../Data/data.Url.json";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ReplyIcon from "@mui/icons-material/Reply";
import SettingsOrgUpdate from "./SettingsOrgUpdate";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SettingsOrgMembers from "./SettingsOrgMembers";
import SettingsOrgInvitation from "./SettingsOrgInvitation";
import SettingsOrgInviteTeam from "./SettingsOrgInviteTeam";
import LinearProgress from "@mui/material/LinearProgress";
import { EasyToastify, ToastContainer } from "../../../Extentions/EasyToastify";

const StyledBox = styled(Box)`
  @media only screen and (max-width: 480px) {
    padding: 16px;
  }

  form {
    width: 540px;
    display: flex;
    flex-direction: column;
    gap: 14px;

    @media only screen and (max-width: 480px) {
      width: 100%;
    }
  }

  label {
    color: #b7b7b7;
    font-size: 16px;
  }

  input {
    padding: 8px;
    border-radius: 8px;
    border: none;
    outline: 0;
    color: #fff;
    background-color: #0b0f1a;
    width: 100%;
  }
`;

const StyledProfileEditBox = styled(Box)`
  position: relative;

  input[type="file"] {
    display: none;
  }

  label {
    position: absolute;
    cursor: pointer;
    bottom: 0;
    left: 90px;
    color: #042045;
    background-color: #fff;
    padding: 7px;
    border-radius: 30px;
    display: flex;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const StyledModalInput = styled.input`
  border: none;

  :focus {
    outline: none;
  }
`;

const StyledModalTextarea = styled.textarea`
  border: none;
  resize: none;

  :focus {
    outline: none;
  }
`;

const StyledCreateOrg = styled.div`
  display: flex;
  flex-direction: column;
  width: 540px;
  gap: 7px;
  padding-bottom: 16px;

  @media only screen and (max-width: 480px) {
    width: auto;
  }
`;

const StyledModalBox = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 640px;
  background-color: #010c20;
  border: 1px solid #fff;
  border-radius: 20px;
  box-shadow: 24px;
  padding: 32px;

  @media only screen and (max-width: 480px) {
    padding: 12px;
    width: 320px;
  }
`;

const StyledLoadingDiv = styled.div`
  width: 580px;

  @media only screen and (max-width: 480px) {
    width: auto;
  }
`;

const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png"];
const validationSchema = yup.object({
  title: yup
    .string()
    .min(5, "Too small!")
    .max(60, "Too long string")
    .required("Title is required!"),
  date: yup.date().required("Date is required"),
  shortDescription: yup
    .string()
    .min(5, "Too small!")
    .max(200, "Too long string")
    .required("Short Description is required"),
  description: yup
    .string()
    .min(20, "Too small!")
    .max(4000, "Too long string")
    .required("Description is required"),
  file: yup
    .mixed()
    .nullable()
    .required()
    .test(
      "FILE_SIZE",
      "Upload file is too big.",
      (value) => !value || (value && value.size <= 2048 * 2048)
    )
    .test(
      "FILE_FORMAT",
      "Upload file has unsupported format.",
      (value) => !value || (value && SUPPORTED_FORMATS.includes(value?.type))
    ),
});

const SettingsOrg = () => {
  const auth = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const axiosPrivateForm = useAxiosPrivateForm();
  // Organization Modal
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  // Profile picture upload
  const [selectedFile, setSelectedFile] = useState();
  const [preview, setPreview] = useState();
  //
  const [organisationData, setOrganisationData] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [registerData, setRegisterData] = useState({
    imgUrl: null,
    about: "",
    country: "",
    name: "",
  });
  const [refresh, setRefresh] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("imgUrl", selectedFile ? selectedFile : null);
    formData.append("about", registerData.about);
    formData.append("country", registerData.country);
    formData.append("name", registerData.name);

    await axiosPrivateForm
      .post("Organisation/Create", formData)
      .then((res) => {
        setRegisterData({
          imgUrl: null,
          about: "",
          country: "",
          name: "",
        });
        setRefresh(!refresh);
        handleClose();
        EasyToastify.ToastSuccess("Organisation Created 🎉");
      })
      .catch((error) => {
        EasyToastify.ToastError("Oop Something wrong!");
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRegisterData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);
  const onSelectFile = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }
    setSelectedFile(e.target.files[0]);
  };

  const onLeaveOrg = async (e) => {
    e.preventDefault();
    if (window.confirm("Do you want to leave organisation?")) {
      await axiosPrivate
        .put("Organisation/Leave")
        .catch((error) => {
          EasyToastify.ToastError("Oops something is wrong!");
        })
        .then((res) => {
          setRefresh(!refresh);
          setOrganisationData(null);
          EasyToastify.ToastSuccess("You left organisation!");
        });
    }
  };

  useEffect(() => {
    const getOrganisation = async () => {
      const response = await axiosPrivate
        .get("Organisation/Get")
        .finally(() => {
          setIsLoaded(true);
        });
      setOrganisationData(response.data);
    };
    getOrganisation();
  }, [refresh]);

  return (
    <StyledBox>
      <Typography variant="h2" paddingBottom="12px">
        Organisation Profile
      </Typography>
      {isLoaded ? (
        organisationData ? (
          <Box display="flex" flexDirection="column" gap="16px">
            <SettingsOrgUpdate organisationData={organisationData} />
            <SettingsOrgMembers />
            <SettingsOrgInviteTeam />
            <Tooltip
              title="Think carefully before you leave"
              placement="bottom-end"
              arrow
            >
              <StyledButton
                onClick={onLeaveOrg}
                variant="contained"
                color="error"
              >
                <span>Leave Organisation</span>
                <ExitToAppIcon />
              </StyledButton>
            </Tooltip>
          </Box>
        ) : (
          <StyledCreateOrg>
            <Tooltip
              title="You can only create one organisation"
              placement="bottom-end"
              arrow
            >
              <StyledButton
                onClick={handleOpen}
                variant="contained"
                color="secondary"
              >
                <span>Create Organisation</span>
                <GroupAddIcon />
              </StyledButton>
            </Tooltip>
            <SettingsOrgInvitation />
          </StyledCreateOrg>
        )
      ) : (
        <StyledLoadingDiv>
          <LinearProgress color="secondary" />
        </StyledLoadingDiv>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <StyledModalBox>
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            <Box display="flex" flexDirection="column" gap="8px">
              <label>Organisation Profile Picture</label>
              <StyledProfileEditBox>
                {preview ? (
                  selectedFile && (
                    <Avatar
                      src={preview}
                      alt={auth.auth.name}
                      sx={{ width: 120, height: 120 }}
                    />
                  )
                ) : (
                  <Avatar
                    alt={auth.auth.name}
                    sx={{ width: 120, height: 120 }}
                  />
                )}
                <label htmlFor="file-upload">
                  <AddAPhotoIcon />
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept="image/png, image/jpeg"
                  onChange={onSelectFile}
                />
              </StyledProfileEditBox>
              <p>Recommend image ratio is 1:1</p>
            </Box>
            <Box display="flex" flexDirection="column">
              <label>Organisation Name</label>
              <StyledModalInput
                type="text"
                onChange={handleChange}
                name="name"
                style={{
                  width: "100%",
                  borderRadius: "6px",
                  padding: "4px 8px",
                }}
              />
            </Box>
            <Box display="flex" flexDirection="column">
              <label>Nationality</label>
              <StyledModalInput
                type="text"
                onChange={handleChange}
                name="country"
                placeholder="example: GE"
                style={{
                  width: "100%",
                  borderRadius: "6px",
                  padding: "4px 8px",
                }}
              />
            </Box>
            <Box display="flex" flexDirection="column">
              <label>Bio &#40;not required&#41;</label>
              <StyledModalTextarea
                type="text"
                onChange={handleChange}
                name="about"
                placeholder="aq unda iyos biogragia"
                rows="4"
                style={{
                  width: "100%",
                  borderRadius: "6px",
                  padding: "4px 8px",
                }}
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              sx={{ width: "100%" }}
            >
              Create
            </Button>
          </form>
        </StyledModalBox>
      </Modal>
      <ToastContainer />
    </StyledBox>
  );
};

export default SettingsOrg;
